import { containsAnyIgnoreCase } from '@dte/otw-utils-core/src/strings/equalsIgnoreCase';
import { StandardObject } from '../../../interfaces/api/CustomerReport';
import { EligibilityResult } from '../../../interfaces/api/EligibilityResult';
import type { ValidatedCustomerReportData } from '../../../interfaces/containers/premises/ValidatedCustomerReportData';
import type { ValidatedProblem } from '../../../interfaces/containers/premises/ValidatedProblem';
import { isTrafficSignalProblem } from './isTrafficSignalProblem';
import { isWireDownOrWithinReach } from './isWireDownOrWithinReach';
import { treeTrimIssue } from './treeNeedsTrimming';

const symptomMappings: Record<string, (problem: ValidatedProblem, eligibility?: EligibilityResult) => boolean> = {};

symptomMappings['PPWD'] = (problem: ValidatedProblem): boolean => {
	// Wire must be down or within reach
	const downOrWithinReach = isWireDownOrWithinReach(problem?.symptoms?.poleToPoleWire);
	if (!downOrWithinReach) {
		return false;
	}

	return true;
};

symptomMappings['XCURR'] = (problem: ValidatedProblem, eligibility?: EligibilityResult): boolean => {
	const outage = Boolean(problem?.symptoms?.power?.outage);
	if (!outage) {
		return false;
	}

	// If we're reporting at a premise, make sure it's active (if we expect the power to be out, we'll report something else)
	if (eligibility !== undefined) {
		if (eligibility !== EligibilityResult.ElectricActive) {
			return false;
		}
	}

	return true;
};

symptomMappings['WDPF'] = (problem: ValidatedProblem): boolean => {
	// Police / fire must be standing by
	const policeFireStandingBy = Boolean(problem?.symptoms?.safety?.policeFireStandingBy);
	if (!policeFireStandingBy) {
		return false;
	}

	// As long as there's at least one type of wire down issue
	const poleToPoleIssue = isWireDownOrWithinReach(problem?.symptoms?.poleToPoleWire);
	if (poleToPoleIssue) {
		return true;
	}

	const poleToBuildingIssue = isWireDownOrWithinReach(problem?.symptoms?.poleToBuildingWire);
	if (poleToBuildingIssue) {
		return true;
	}

	return false;
};

symptomMappings['MISCPF'] = (problem: ValidatedProblem): boolean => {
	// Police / fire must be standing by
	const policeFireStandingBy = Boolean(problem?.symptoms?.safety?.policeFireStandingBy);
	if (!policeFireStandingBy) {
		return false;
	}

	return true;
};

symptomMappings['SDXL'] = (problem: ValidatedProblem, eligibility?: EligibilityResult): boolean => {
	// If we're reporting at a premise, make sure it's active (if we expect the power to be out, we'll report SDLTSOK)
	if (eligibility !== undefined) {
		if (eligibility !== EligibilityResult.ElectricActive) {
			return false;
		}
	}

	// Wire must be down or within reach
	const downOrWithinReach = isWireDownOrWithinReach(problem?.symptoms?.poleToBuildingWire);
	if (!downOrWithinReach) {
		return false;
	}

	// Power must be out
	const outage = Boolean(problem?.symptoms?.power?.outage);
	if (!outage) {
		return false;
	}

	return true;
};

symptomMappings['SDLTSOK'] = (problem: ValidatedProblem): boolean => {
	// Wire must be down or within reach
	const downOrWithinReach = isWireDownOrWithinReach(problem?.symptoms?.poleToBuildingWire);
	if (!downOrWithinReach) {
		return false;
	}

	return true;
};

symptomMappings['TT'] = (problem: ValidatedProblem): boolean => {
	// There's a tree on the wire that needs trimming
	const needsTrimming = treeTrimIssue(problem);
	if (!needsTrimming) {
		return false;
	}

	return true;
};

symptomMappings['HZ'] = (_problem: ValidatedProblem): boolean => {
	// TODO: this needs to have real criteria in order to map the non hazard call codes from CR reporting

	// This is the fallback code at the end of the list
	return true;
};

symptomMappings['NONHZD'] = (_problem: ValidatedProblem): boolean => {
	// TODO: this should only be true for the V3 call code mapping

	// This is the fallback code at the end of the list
	return false;
};

symptomMappings['ARC'] = (problem: ValidatedProblem): boolean => {
	// Make sure at least one object / location is marked as arcing
	const arcing = Boolean(problem?.symptoms?.arcing);
	if (!arcing) {
		return false;
	}

	return true;
};

symptomMappings['CONTACT'] = (problem: ValidatedProblem): boolean => {
	const contact = Boolean(problem?.symptoms?.safety?.contactWithEnergizedEquipment);
	if (!contact) {
		return false;
	}

	return true;
};

symptomMappings['TRAPPED'] = (problem: ValidatedProblem): boolean => {
	const trapped = Boolean(problem?.symptoms?.safety?.publicTrapped);
	if (!trapped) {
		return false;
	}

	return true;
};

symptomMappings['LOWVOL'] = (problem: ValidatedProblem): boolean => {
	const dimOrBrightLights = Boolean(problem?.symptoms?.power?.dimOrBrightLights);
	if (!dimOrBrightLights) {
		return false;
	}

	return true;
};

symptomMappings['ONELEG'] = (problem: ValidatedProblem): boolean => {
	const partialOutage = Boolean(problem?.symptoms?.power?.partialOutage);
	if (!partialOutage) {
		return false;
	}

	return true;
};

symptomMappings['FLICKER'] = (problem: ValidatedProblem): boolean => {
	const flickeringLights = Boolean(problem?.symptoms?.power?.flickeringLights);
	if (!flickeringLights) {
		return false;
	}

	return true;
};

symptomMappings['TRAFCSIGPF'] = (problem: ValidatedProblem): boolean => {
	// Police / fire must be standing by
	const policeFireStandingBy = Boolean(problem?.symptoms?.safety?.policeFireStandingBy);
	if (!policeFireStandingBy) {
		return false;
	}

	// Something is wrong with the traffic signal
	const trafficSignal = isTrafficSignalProblem(problem);
	if (!trafficSignal) {
		return false;
	}

	return true;
};

symptomMappings['PTFIRE'] = (problem: ValidatedProblem): boolean => {
	// Something is on fire
	const fire = Boolean(problem?.symptoms?.fire);
	if (!fire) {
		return false;
	}

	const matchingLocations = [StandardObject.Pole, StandardObject.Transformer, StandardObject.UndergroundTransformer];
	const location = problem?.symptoms?.fire?.location;
	const matchesLocation = containsAnyIgnoreCase(location, matchingLocations);
	if (!matchesLocation) {
		return false;
	}

	return true;
};

symptomMappings['BP'] = (problem: ValidatedProblem): boolean => {
	const brokenPole = Boolean(problem?.symptoms?.pole?.broken);
	if (!brokenPole) {
		return false;
	}

	return true;
};

symptomMappings['STRUFIREPF'] = (problem: ValidatedProblem): boolean => {
	// Building or roof are on fire
	const matchingLocations = [StandardObject.Building, StandardObject.Roof];
	const location = problem?.symptoms?.fire?.location;
	const matchesLocation = containsAnyIgnoreCase(location, matchingLocations);
	if (!matchesLocation) {
		return false;
	}

	return true;
};

symptomMappings['UGEXP'] = (problem: ValidatedProblem): boolean => {
	// Underground wire must be exposed
	const undergroundExposed = problem?.symptoms?.undergroundWire?.exposed;
	if (!undergroundExposed) {
		return false;
	}

	return true;
};

export function doesCallCodeMatchReportV1(
	report: ValidatedCustomerReportData,
	callCode: string,
	eligibility: EligibilityResult | undefined,
): boolean {
	const matchFunction = symptomMappings[callCode];
	if (!matchFunction) {
		return false;
	}

	return matchFunction(report?.problem, eligibility);
}
